import React from 'react'
import ContentContainer from '../ContentContainer/ContentContainer.react'
import GallerySlider from '../Gallery.Slider/Gallery.Slider.react'
import { SvgIcon } from '../SvgIcon/SvgIcon.react'

interface GalleryProps {
	thumbnails: string[]
	images: {
		image: string
		title: string
		caption: string
	}[]
}

export default ({ thumbnails, images }: GalleryProps) => {
	const [activeIndex, setActiveIndex] = React.useState(0)
	const [showLightbox, setShowLightbox] = React.useState(false)
	const [touchPosition, setTouchPosition] = React.useState(0)

	const handleThumbnailClick = (index: number) => {
		setActiveIndex(index + 1)
		setShowLightbox(true)
	}

	// Allow users to close lightbox with escape key
	const handleKeyPress = (event: KeyboardEvent) => {
		if (event.key !== 'Escape') {
			return
		}
		setShowLightbox(false)
	}

	// Add / remove eventListener for keypress for closing the lightbox with keyboard
	React.useEffect(() => {
		if (showLightbox) {
			window.addEventListener('keydown', handleKeyPress)
			document.body.style.overflowY = 'hidden'
		} else {
			window.removeEventListener('keydown', handleKeyPress)

			setTimeout(() => {
				document.body.style.removeProperty('overflow-y')
			}, 200)
		}
	}, [showLightbox])

	const handleTouchStart = (event: React.TouchEvent) => {
		if (!showLightbox) {
			return
		}
		const touchDown = event.touches[0].screenY

		setTouchPosition(touchDown)
	}

	const handleTouchMove = (event: React.TouchEvent) => {
		if (touchPosition === 0 || !showLightbox) {
			return
		}

		if (event.touches[0].screenY - touchPosition > 250) {
			setShowLightbox(false)
		}
	}

	// Map images to slides for slider
	const slides = images.map(
		(image: { image: string; title: string; caption: string }, index: number) => {
			return (
				<div key={index} className="w-full flex-shrink-0 flex-grow-1">
					<div
						dangerouslySetInnerHTML={{ __html: image.image }}
						className="w-full h-full lg:max-w-4/5 mx-auto"
					></div>
				</div>
			)
		}
	)

	return (
		<div>
			<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
				{thumbnails.map((thumbnail: string, index: number) => (
					<div
						key={index}
						className="w-full cursor-pointer"
						onClick={() => handleThumbnailClick(index)}
					>
						<div dangerouslySetInnerHTML={{ __html: thumbnail }}></div>
					</div>
				))}
			</div>
			{
				<div
					onTouchStart={(e) => handleTouchStart(e)}
					onTouchMove={(e) => handleTouchMove(e)}
				>
					<Lightbox isVisible={showLightbox}>
						<GallerySlider infiniteLoop show={1} showSlide={activeIndex}>
							{slides}
						</GallerySlider>
						<button
							type="button"
							onClick={() => setShowLightbox(false)}
							className="absolute z-10 p-4 top-0 right-0 lg:top-8 lg:right-24 cursor-pointer text-white"
						>
							<SvgIcon
								collectionName="seebad"
								iconName="close"
								className="w-4 h-4 lg:w-5 lg:h-5"
							/>
						</button>
					</Lightbox>
				</div>
			}
		</div>
	)
}

const Lightbox = (props: { isVisible: boolean; children: React.ReactNode }) => {
	return (
		<div
			className={`fixed z-50 w-screen h-screen top-0 left-0  flex items-center justify-center ${props.isVisible ? 'mt-0 bg-anthrazit' : '-mt-screen bg-transparent'}`}
		>
			<div
				className={`w-full h-full relative flex items-center justify-center py-4 ${props.isVisible ? 'opacity-100 translate-y-0 transition-transform duration-500' : 'opacity-0 -translate-y-1/3 transition-transform duration-500'}`}
			>
				{props.children}
			</div>
		</div>
	)
}
